import { z } from 'zod';
// ── commission barems (per-shop tiered commission) ───────────────────────────
export const commissionTierModeSchema = z.enum(['marginal', 'cliff']);
// A single band. `threshold` is the band's lower bound (marginal) / unlock level
// (cliff), in MUR. `ratePct` is a FRACTION (0.0075 = 0.75%).
export const commissionBaremTierSchema = z.object({
    threshold: z.coerce.number().min(0).max(1_000_000_000),
    ratePct: z.coerce.number().min(0).max(1),
});
export const commissionBaremUpsertSchema = z
    .object({
    tierMode: commissionTierModeSchema.default('marginal'),
    // Rate applied below the first band (when no/empty tiers, this is the flat rate).
    fallbackRatePct: z.coerce.number().min(0).max(1).default(0),
    // Net must reach this before any commission is earned (0 = no gate).
    qualifyingMin: z.coerce.number().min(0).max(1_000_000_000).default(0),
    // Legacy single target, kept for the progress bar on the commission page.
    targetMonthly: z.coerce.number().min(0).nullable().optional(),
    effectiveFrom: z.coerce.date().optional(),
    tiers: z.array(commissionBaremTierSchema).max(30).default([]),
})
    .superRefine((val, ctx) => {
    // Thresholds must be strictly ascending (and therefore unique).
    for (let i = 1; i < val.tiers.length; i++) {
        if (val.tiers[i].threshold <= val.tiers[i - 1].threshold) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Band thresholds must be in strictly ascending order',
                path: ['tiers', i, 'threshold'],
            });
        }
    }
});
// ── condition modifiers ───────────────────────────────────────────────────────
// (b) Direct-commission models (Torres, Swisspedic, Memory Gel): the salesperson
// earns this FRACTION (0.02 = 2%) of line net directly — excluded from the shop
// pool — payable once the order is fully paid AND delivered. null clears it.
export const modelDirectRateSchema = z.object({
    directCommissionPct: z.coerce.number().min(0).max(1).nullable(),
});
// (c) Online-channel rate per category: the salesperson on an ONLINE-shop order
// earns this FRACTION of line net (0.01 imported / 0.005 local mattresses),
// halved automatically on promo/discounted lines. null = no online commission.
export const categoryOnlineRateSchema = z.object({
    onlineCommissionPct: z.coerce.number().min(0).max(1).nullable(),
});
// (d) Cross-sales commission: every mattress sale should pull accessory sales
// (protectors, pillows, toppers, duvets, bed sheets, other approved accessories).
// When a shop MISSES its qualifying target so the barem pool stays locked, the
// salesperson still earns this FRACTION (5%) of the active net of the eligible
// cross-sales lines they sold. When the shop qualifies, normal pool commission
// applies instead and the floor is not paid. Eligibility is a per-Product flag.
// NOTE: keep in sync with CROSS_SALE_PCT in the backend commission engine.
export const CROSS_SALE_COMMISSION_PCT = 0.05;
export const productCrossSaleSchema = z.object({
    crossSaleEligible: z.boolean(),
});
//# sourceMappingURL=commissions.js.map